import { useEffect, useMemo, useCallback, useRef } from "react";
import useMultiSelector from "../../../hooks/useMultiSelector";
import useLang from "../../../hooks/useLang";

const useFormMultiselector = ({
  id,
  initialValue,
  options,
  onChange: handleChange,
  idAsValue,
  label,
}) => {
  const lang = useLang();
  const hasInitialized = useRef(false);
  const prevInitial = useRef(initialValue);

  const getOptionWithIdAndLabel = useCallback(
    (option, idx) => {
      if (typeof option === "object" && idAsValue ? option.id : option.label) {
        let result = {
          id:
            option.id ||
            `${option.slice(0, Math.min(3, option.label.length))}_${idx}`,
          label: option.label || option.id,
        };
        if (option.details != null) {
          result.details = option.details;
        }
        if (option.tag != null) {
          result.tag = option.tag;
        }
        if (option.disabled != null) {
          result.disabled = option.disabled;
        }
        return result;
      } else if (typeof option === "string") {
        return {
          id: idAsValue
            ? option
            : `${option.slice(0, Math.min(3, option.length))}_${idx}`,
          label: option,
        };
      } else {
        return { id: idAsValue ? option : idx, label: option };
      }
    },
    [idAsValue]
  );

  const selectorOptions = useMemo(() => {
    let result = [];
    if (Array.isArray(options)) {
      result = options.map((option, idx) => {
        return getOptionWithIdAndLabel(option, idx);
      });
    }
    return result;
  }, [options, getOptionWithIdAndLabel]);

  const getValueWithId = useCallback(
    (value, options) => {
      const valueArray = Array.isArray(value) ? [...value] : [value];
      let i = 0;
      const result = valueArray?.map((value) => {
        if (typeof value === "string") {
          const correspondingOption = options?.find((option) =>
            idAsValue ? option.id === value : option.label === value
          );
          if (correspondingOption != null) return correspondingOption;
          const valueWithId = getOptionWithIdAndLabel(
            value,
            options.length + i
          );
          i++;
          return valueWithId;
        }
        return value;
      });
      return result;
    },
    [getOptionWithIdAndLabel, idAsValue]
  );

  const initialValueArray = useMemo(() => {
    let hydrated =
      initialValue != null ? getValueWithId(initialValue, selectorOptions) : [];

    // Auto-select all options with tag 'default'
    const autoSelected = selectorOptions.filter((opt) => opt.tag === "default");

    // Merge without duplicates
    const merged = [
      ...hydrated,
      ...autoSelected.filter((opt) => !hydrated.some((v) => v.id === opt.id)),
    ];

    return merged.length > 0 ? merged : undefined;
  }, [initialValue, selectorOptions, getValueWithId]);

  const selector = useMultiSelector({
    options: selectorOptions,
    defaultValue: initialValueArray,
  });

  useEffect(() => {
    const isDifferent =
      selector.options.length !== selectorOptions.length ||
      selector.options.some((opt, idx) => opt.id !== selectorOptions[idx]?.id);

    if (isDifferent) {
      selector.reset(selectorOptions, selector.value);
    }
  }, [selectorOptions, selector]);

  useEffect(() => {
    const initialChanged = prevInitial.current !== initialValue;
    const isDifferent =
      selector.value?.length !== initialValueArray?.length ||
      selector.value?.some((v, i) => v.id !== initialValueArray?.[i]?.id);

    if (
      !hasInitialized.current &&
      initialValueArray &&
      (initialChanged || isDifferent)
    ) {
      selector.setValue(initialValueArray);
      hasInitialized.current = true;
      prevInitial.current = initialValue;
    }
    // eslint-disable-next-line
  }, [initialValueArray, selector.value, initialValue]);

  useEffect(() => {
    if (typeof handleChange === "function") {
      const newValue = {
        id: id,
        value: selector.value
          ?.map((value) => (idAsValue ? value?.id : value?.label))
          .filter((value) => value != null),
      };
      handleChange(newValue);
    }
    // eslint-disable-next-line
  }, [JSON.stringify(selector.value)]);

  useEffect(() => {
    hasInitialized.current = false;
  }, [id]);

  return {
    key: id,
    label: label
      ? label
      : label === null || label === false
      ? null
      : lang(`captions.${id}`),
    type: "text",
    value: selector.value,
    options: selector.options,
    onChange: selector.onChange,
    ...selector.attributes,
  };
};

export default useFormMultiselector;
